import { useCallback, useMemo, useRef, useState } from 'react';
import { fmt } from '../lib/money.js';

// Round axis ticks: 0, 1 000, 2 000…
function niceTicks(min, max, count = 4) {
  if (min === max) { max = min + 1; }
  const span = max - min;
  const step0 = span / count;
  const mag = 10 ** Math.floor(Math.log10(step0));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= step0) || 10 * mag;
  const lo = Math.floor(min / step) * step;
  const hi = Math.ceil(max / step) * step;
  const ticks = [];
  for (let v = lo; v <= hi + step / 2; v += step) ticks.push(Math.round(v * 100) / 100);
  return ticks;
}
const compact = (v) => {
  const a = Math.abs(v);
  if (a >= 1e6) return `${(v / 1e6).toFixed(1).replace(/\.0$/, '')}M`;
  if (a >= 1e4) return `${Math.round(v / 1e3)}k`;
  if (a >= 1e3) return `${(v / 1e3).toFixed(1).replace(/\.0$/, '')}k`;
  return String(Math.round(v));
};

function useWidth() {
  const [w, setW] = useState(640);
  const observer = useRef(null);
  const set = useCallback((el) => {
    if (observer.current) { observer.current.disconnect(); observer.current = null; }
    if (!el || typeof ResizeObserver !== 'function') return;
    observer.current = new ResizeObserver(([e]) => setW(Math.max(280, Math.round(e.contentRect.width))));
    observer.current.observe(el);
  }, []);
  return [set, w];
}

// Savings balance: actual up to now, then projected (lighter). One series, so no legend.
export function BalanceChart({ points, height = 220, label = 'Savings balance' }) {
  const [setRef, width] = useWidth();
  const [hover, setHover] = useState(null);
  const pad = { l: 44, r: 12, t: 12, b: 26 };
  const geo = useMemo(() => {
    if (!points.length) return null;
    const vals = points.map((p) => p.value);
    const ticks = niceTicks(Math.min(0, ...vals), Math.max(0, ...vals));
    const lo = ticks[0];
    const hi = ticks[ticks.length - 1];
    const iw = width - pad.l - pad.r;
    const ih = height - pad.t - pad.b;
    const x = (i) => pad.l + (points.length === 1 ? iw / 2 : (i / (points.length - 1)) * iw);
    const y = (v) => pad.t + ih - ((v - lo) / (hi - lo || 1)) * ih;
    const split = points.findIndex((p) => p.projected);
    const actual = split === -1 ? points : points.slice(0, split);
    const proj = split === -1 ? [] : points.slice(Math.max(0, split - 1));
    const line = (arr, offset) => arr.map((p, i) => `${i ? 'L' : 'M'}${x(i + offset).toFixed(1)},${y(p.value).toFixed(1)}`).join('');
    const area = actual.length > 1 ? `${line(actual, 0)}L${x(actual.length - 1)},${y(Math.max(lo, 0))}L${x(0)},${y(Math.max(lo, 0))}Z` : '';
    return { ticks, x, y, actualPath: line(actual, 0), projPath: proj.length > 1 ? line(proj, Math.max(0, split - 1)) : '', area, lastActual: actual.length - 1, iw, ih };
  }, [points, width, height]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!geo) return <div className="empty">Nothing to show yet</div>;
  const step = Math.max(1, Math.ceil(points.length / Math.max(2, Math.floor(width / 70))));
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const px = ((e.clientX - r.left) / r.width) * width;
    const i = Math.round(((px - pad.l) / (geo.iw || 1)) * (points.length - 1));
    setHover(Math.max(0, Math.min(points.length - 1, i)));
  };
  const hp = hover !== null ? points[hover] : null;
  return (
    <div className="chart" ref={setRef}>
      <svg viewBox={`0 0 ${width} ${height}`} height={height} role="img" aria-label={`${label}, ${points.length} months`} onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
        {geo.ticks.map((t) => (
          <g key={t}>
            <line className={t === 0 ? 'baseline' : 'gridline'} x1={pad.l} x2={width - pad.r} y1={geo.y(t)} y2={geo.y(t)} />
            <text className="tick" x={pad.l - 6} y={geo.y(t) + 4} textAnchor="end">{compact(t)}</text>
          </g>
        ))}
        {points.map((p, i) => (i % step === 0 ? <text key={p.key} className="tick" x={geo.x(i)} y={height - 6} textAnchor="middle">{p.short}</text> : null))}
        {geo.area && <path d={geo.area} fill="var(--accent)" opacity="0.1" />}
        <path d={geo.actualPath} fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        {geo.projPath && <path d={geo.projPath} fill="none" stroke="var(--accent)" strokeOpacity="0.45" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />}
        {geo.lastActual >= 0 && <circle cx={geo.x(geo.lastActual)} cy={geo.y(points[geo.lastActual].value)} r="4.5" fill="var(--accent)" stroke="var(--surface)" strokeWidth="2" />}
        {hp && (
          <g>
            <line x1={geo.x(hover)} x2={geo.x(hover)} y1={pad.t} y2={height - pad.b} stroke="var(--axis)" />
            <circle cx={geo.x(hover)} cy={geo.y(hp.value)} r="4.5" fill="var(--accent)" fillOpacity={hp.projected ? 0.6 : 1} stroke="var(--surface)" strokeWidth="2" />
          </g>
        )}
      </svg>
      {hp && (
        <div className="chart-tip" style={{ left: `${(geo.x(hover) / width) * 100}%`, top: geo.y(hp.value) }}>
          <div className="dim">{hp.label}{hp.projected ? ' · projected' : ''}</div>
          <div className="num" style={{ fontWeight: 650 }}>{fmt(hp.value)}</div>
        </div>
      )}
      <div className="legend small" style={{ marginTop: 6 }}>
        <span><svg width="18" height="6"><line x1="0" x2="18" y1="3" y2="3" stroke="var(--accent)" strokeWidth="2" /></svg>Actual</span>
        <span><svg width="18" height="6"><line x1="0" x2="18" y1="3" y2="3" stroke="var(--accent)" strokeOpacity="0.45" strokeWidth="2" /></svg>Projected</span>
      </div>
    </div>
  );
}

// Grouped columns per month (e.g. expected vs actual savings). Negative values hang below the baseline.
export function ColumnChart({ groups, series, height = 220, label }) {
  const [setRef, width] = useWidth();
  const [hover, setHover] = useState(null);
  const pad = { l: 44, r: 8, t: 12, b: 26 };
  const vals = groups.flatMap((g) => series.map((s) => g.values[s.id] ?? 0));
  const ticks = niceTicks(Math.min(0, ...vals), Math.max(0, ...vals));
  const lo = ticks[0];
  const hi = ticks[ticks.length - 1];
  const iw = width - pad.l - pad.r;
  const ih = height - pad.t - pad.b;
  const band = iw / Math.max(1, groups.length);
  const gap = 2;
  const barW = Math.max(3, Math.min(24, (band * 0.7 - gap * (series.length - 1)) / series.length));
  const y = (v) => pad.t + ih - ((v - lo) / (hi - lo || 1)) * ih;
  const y0 = y(0);
  const step = Math.max(1, Math.ceil(groups.length / Math.max(2, Math.floor(width / 60))));
  const bar = (x, v, w) => {
    const top = Math.min(y(v), y0);
    const h = Math.max(0.5, Math.abs(y(v) - y0));
    const r = Math.min(4, h, w / 2);
    // rounded at the data end, square at the baseline
    if (v >= 0) return `M${x},${top + h}V${top + r}Q${x},${top} ${x + r},${top}H${x + w - r}Q${x + w},${top} ${x + w},${top + r}V${top + h}Z`;
    return `M${x},${top}V${top + h - r}Q${x},${top + h} ${x + r},${top + h}H${x + w - r}Q${x + w},${top + h} ${x + w},${top + h - r}V${top}Z`;
  };
  const hg = hover !== null ? groups[hover] : null;
  return (
    <div className="chart" ref={setRef}>
      {series.length > 1 && (
        <div className="legend" style={{ marginBottom: 6 }}>
          {series.map((s) => <span key={s.id}><span className="swatch" style={{ background: s.color }} />{s.label}</span>)}
        </div>
      )}
      <svg viewBox={`0 0 ${width} ${height}`} height={height} role="img" aria-label={label} onMouseLeave={() => setHover(null)}>
        {ticks.map((t) => (
          <g key={t}>
            <line className={t === 0 ? 'baseline' : 'gridline'} x1={pad.l} x2={width - pad.r} y1={y(t)} y2={y(t)} />
            <text className="tick" x={pad.l - 6} y={y(t) + 4} textAnchor="end">{compact(t)}</text>
          </g>
        ))}
        {groups.map((g, i) => {
          const gx = pad.l + i * band + (band - (barW * series.length + gap * (series.length - 1))) / 2;
          return (
            <g key={g.key} onMouseEnter={() => setHover(i)}>
              <rect x={pad.l + i * band} y={pad.t} width={band} height={ih} fill={hover === i ? 'rgba(255,255,255,0.04)' : 'transparent'} />
              {series.map((s, j) => {
                const v = g.values[s.id];
                if (v === null || v === undefined) return null;
                return <path key={s.id} d={bar(gx + j * (barW + gap), v, barW)} fill={s.color} opacity={g.dim ? 0.45 : 1} />;
              })}
              {i % step === 0 && <text className="tick" x={pad.l + i * band + band / 2} y={height - 6} textAnchor="middle">{g.short}</text>}
            </g>
          );
        })}
      </svg>
      {hg && (
        <div className="chart-tip" style={{ left: `${((pad.l + hover * band + band / 2) / width) * 100}%`, top: pad.t + 20 }}>
          <div className="dim">{hg.label}{hg.dim ? ' · projected' : ''}</div>
          {series.map((s) => (hg.values[s.id] !== null && hg.values[s.id] !== undefined ? (
            <div key={s.id} className="row" style={{ gap: 6 }}><span className="swatch" style={{ background: s.color }} /><span>{s.label}</span><span className="spacer" /><span className="num" style={{ fontWeight: 600 }}>{fmt(hg.values[s.id])}</span></div>
          ) : null))}
        </div>
      )}
    </div>
  );
}
